// The staff upload queue (Invariant 4).
//
// Captures are written to IndexedDB RAW and immediately — compression and upload
// both happen later, in the drain worker, at concurrency 2. The queue rehydrates
// on load, so a mid-burst refresh, a locked phone or a dead tunnel costs nothing
// but time.
//
// Every step is idempotent, so a retry never has to reason about how far the
// previous attempt got: re-issue (insert-if-absent), re-upload (upsert), and
// re-confirm (no-op when already ready).
import {
  ApiError,
  confirmUpload,
  issueUpload,
  putSigned,
} from "./api";
import {
  getUpload,
  pendingUploads,
  pruneSynced,
  putUpload,
  putUploadsBatch,
  type UploadKind,
  type UploadRecord,
} from "./db";
// staffToken() is read at SEND time, never captured: the session can be
// re-established mid-drain (token expires, operator signs back in) and the
// next attempt must pick up the new one. A missing token is a RETRYABLE
// error, so a queued photo waits for a sign-in instead of failing.
import { staffToken } from "./session";
import { compress } from "./compress";
import { uuid } from "./ids";

const CONCURRENCY = 2;
const INTERVAL_MS = 20_000;
const MAX_ATTEMPTS = 12;

type Listener = () => void;
const listeners = new Set<Listener>();

export function onQueueChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // A broken subscriber must never break the queue.
    }
  }
}

function nextDelayMs(attempts: number): number {
  const base = Math.min(2 ** attempts * 1000, 5 * 60_000);
  return base * (0.5 + Math.random() * 0.5);
}

function isDue(u: UploadRecord, now: number): boolean {
  if (u.attempts === 0 || !u.lastAttemptAt) return true;
  return now >= new Date(u.lastAttemptAt).getTime() + nextDelayMs(u.attempts);
}

/* --------------------------------- ingest ---------------------------------- */

/**
 * Persists a batch of captures. RAW ONLY — no compression here.
 *
 * This was rewritten after the 200-photo rehearsal caught a real defect: when
 * ingest compressed each file first, a refresh mid-batch destroyed every file
 * that had not been reached yet (171 of 200 in the reproduction). Those photos
 * existed only as an in-memory FileList, so nothing could recover them and
 * staff got no error — they simply never arrived in the guest's gallery.
 *
 * Now the only work between "staff picked the files" and "it is durable" is one
 * IndexedDB transaction. Compression happens later, in the drain worker, where
 * an interruption costs nothing.
 *
 * The trade-off is transient storage: originals sit on the device until the
 * worker compresses them and drops the raw blob. That is bounded and it drains
 * continuously; losing a guest's photo is not recoverable at all.
 */
export async function enqueueMany(args: {
  guestId: string;
  guestCode: string;
  kind: UploadKind;
  files: Blob[];
}): Promise<UploadRecord[]> {
  const now = new Date().toISOString();
  const records: UploadRecord[] = args.files.map((file) => ({
    id: uuid(),
    guestId: args.guestId,
    guestCode: args.guestCode,
    kind: args.kind,
    raw: file,
    web: null,
    thumb: null,
    bytes: file.size,
    createdAt: now,
    sync: "pending",
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
  }));

  await putUploadsBatch(records);
  notify();
  void drainUploads();
  return records;
}

/** Single-capture convenience over the same durable path. */
export async function enqueue(args: {
  guestId: string;
  guestCode: string;
  kind: UploadKind;
  file: Blob;
}): Promise<UploadRecord> {
  const [rec] = await enqueueMany({ ...args, files: [args.file] });
  return rec;
}

/* ---------------------------------- drain ---------------------------------- */

let draining = false;
let timer: ReturnType<typeof setInterval> | null = null;

async function sendOne(rec: UploadRecord): Promise<void> {
  const now = new Date().toISOString();

  try {
    // Compress on first send. Doing it here rather than at ingest is what makes
    // a mid-batch refresh survivable — see enqueueMany.
    if (!rec.web && rec.raw) {
      const { web, thumb } = await compress(rec.raw);
      rec = {
        ...rec,
        web,
        // Both kinds are gallery content and both get a thumbnail: the grid
        // is driven by thumbs, and a brought-in image that skipped one would
        // load at full size in every tile.
        thumb,
        bytes: web.size,
        raw: null, // free the original immediately
      };
      await putUpload(rec);
    }

    if (!rec.web) {
      // Nothing to send: blobs were pruned after an earlier successful upload.
      await putUpload({ ...rec, sync: "synced", lastError: null });
      return;
    }

    // One path for both kinds. They differ only in the provenance the server
    // records, and keeping the upload identical is what stops the thumbnail,
    // upsert and retry behaviour drifting apart between them.
    const token = staffToken();
    if (!token) throw new ApiError("not signed in", 0, true);

    const targets = await issueUpload({
      guestCode: rec.guestCode,
      mediaId: rec.id,
      bytes: rec.bytes,
      contentType: "image/jpeg",
      kind: rec.kind,
    }, token);

    await putSigned(targets.web, rec.web, "image/jpeg");
    if (rec.thumb) await putSigned(targets.thumb, rec.thumb, "image/jpeg");

    await confirmUpload(rec.id, token);

    // Success. Drop the blobs — they are the bulk of the storage cost.
    await putUpload({
      ...rec,
      raw: null,
      web: null,
      thumb: null,
      sync: "synced",
      attempts: rec.attempts + 1,
      lastAttemptAt: now,
      lastError: null,
    });
  } catch (e) {
    const err = e instanceof ApiError ? e : null;
    const permanent = err !== null && !err.retryable;
    await putUpload({
      ...rec,
      // Keep the blobs on any retryable failure — losing them loses the photo.
      sync: permanent || rec.attempts + 1 >= MAX_ATTEMPTS ? "failed" : "pending",
      attempts: rec.attempts + 1,
      lastAttemptAt: now,
      lastError: String(e).slice(0, 300),
    });
  }
}

export async function drainUploads(): Promise<void> {
  if (draining) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  draining = true;

  try {
    const now = Date.now();
    const queue = (await pendingUploads())
      .filter((u) => u.attempts < MAX_ATTEMPTS && isDue(u, now));

    // Fixed-size worker pool: exactly CONCURRENCY in flight, pulling from a
    // shared cursor. A phone on venue Wi-Fi collapses if we fan out wider.
    let cursor = 0;
    const worker = async () => {
      while (cursor < queue.length) {
        const rec = queue[cursor++];
        // Re-read: a parallel worker or a refresh may have advanced this row.
        const fresh = await getUpload(rec.id);
        if (!fresh || fresh.sync === "synced") continue;
        await sendOne(fresh);
        notify();
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker),
    );

    await pruneSynced();
  } finally {
    draining = false;
    notify();
  }
}

/** Puts failed uploads back in the queue (staff "retry all"). */
export async function retryFailed(): Promise<number> {
  const all = await pendingUploads();
  const failed = all.filter((u) => u.sync === "failed");
  for (const rec of failed) {
    await putUpload({ ...rec, sync: "pending", attempts: 0, lastAttemptAt: null });
  }
  notify();
  void drainUploads();
  return failed.length;
}

/** Same four triggers as the registration outbox. Idempotent. */
export function startUploadQueue(): () => void {
  const kick = () => void drainUploads();

  if (timer === null) timer = setInterval(kick, INTERVAL_MS);

  const onVisible = () => {
    if (document.visibilityState === "visible") kick();
  };

  globalThis.addEventListener("online", kick);
  document.addEventListener("visibilitychange", onVisible);

  void navigator.serviceWorker?.ready
    .then((reg) =>
      (reg as ServiceWorkerRegistration & {
        sync?: { register(tag: string): Promise<void> };
      }).sync?.register("laqta-uploads")
    )
    .catch(() => {
      /* unsupported — the interval is the real guarantee */
    });

  // Rehydrate: whatever was mid-flight when the tab died resumes here.
  kick();

  return () => {
    globalThis.removeEventListener("online", kick);
    document.removeEventListener("visibilitychange", onVisible);
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}
